import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Info,
  Loader2,
  Plane,
  Ship,
  Truck,
} from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import { cantidadDe, formatoPorcentaje } from '@/lib/utils/formato-precio';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { MarcoDeComercia } from '../components/marco-de-comercia';
import { EncabezadoDeComercia } from '../components/encabezado-de-comercia';
import { PasosDeLogistica } from '../components/pasos-de-logistica';
import { comerciaService } from '../services/comercia-service';
import { useImportacion } from '../store/importacion-store';
import type { ImportacionDeComercia, OpcionDeRuta, RutasDeComercia } from '../types/comercia.types';
import { sobreElFob, usd } from '../utils/formatos';
import { ciudadDe } from '../utils/lugares';

type Orden = 'barata' | 'rapida';

/**
 * Comercia › Logística › Rutas (maqueta de Melanie): «como si fuera para comprar
 * un pasaje en avión: quiero la más barata, quiero la más rápida». Cada ruta con
 * su desglose (flete, seguro, transporte terrestre, gastos portuarios…) y el
 * total puesto en destino.
 *
 * Los números salen del tarifario. «Big disclaimer»: son tarifas del pasado; la
 * pantalla lo dice y, si alguna está vencida, lo dice en grande.
 */
export function RutasPage() {
  return (
    <MarcoDeComercia>
      <Contenido />
    </MarcoDeComercia>
  );
}

function Contenido() {
  const { importacion, aplicar } = useImportacion();
  const { toast } = useToast();
  const [datos, setDatos] = useState<RutasDeComercia | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [orden, setOrden] = useState<Orden>('barata');
  const [abierta, setAbierta] = useState<string | null>(null);
  const [eligiendo, setEligiendo] = useState<string | null>(null);
  const [todas, setTodas] = useState(false);

  const faltaAlgo =
    !importacion?.origen || !importacion.destino || (importacion?.contenedores.length ?? 0) === 0;

  const cargar = useCallback(async () => {
    if (faltaAlgo) {
      setCargando(false);
      return;
    }
    setCargando(true);
    setError(null);
    try {
      const r = await comerciaService.rutas();
      setDatos(r);
      setAbierta((a) => a ?? r.elegidaId ?? r.opciones[0]?.id ?? null);
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudieron calcular las rutas. Vuelve a intentar.'));
    } finally {
      setCargando(false);
    }
  }, [faltaAlgo]);

  useEffect(() => {
    void cargar();
    // Se recalculan si cambia la carga (contenedores) o el FOB (el seguro sale de ahí).
  }, [cargar, importacion?.resumen.fob, importacion?.contenedores.length]);

  // Si el punto de llegada filtra y se piden todas, se vuelve a pedir sin filtro: el
  // servidor filtra, así que «ver todas» quita el punto de llegada de la importación.
  const verTodas = async () => {
    if (!importacion?.origen || !importacion.destino) return;
    setTodas(true);
    try {
      aplicar(
        await comerciaService.guardarOrigenDestino({
          origenId: importacion.origen.id,
          destinoId: importacion.destino.id,
          puntoLlegadaId: null,
          almacen: importacion.almacen,
        })
      );
      await cargar();
    } finally {
      setTodas(false);
    }
  };

  const elegir = async (o: OpcionDeRuta) => {
    setEligiendo(o.id);
    try {
      aplicar(await comerciaService.elegirRuta(o.id));
      setDatos((d) => (d ? { ...d, elegidaId: o.id } : d));
      toast({
        title: `Ruta elegida: vía ${o.via ?? 'directa'}`,
        description: o.transportistas.join(' + '),
      });
    } catch (e) {
      toast({
        title: 'No se pudo elegir la ruta',
        description: mensajeDeError(e, 'Vuelve a intentar.'),
        variant: 'destructive',
      });
      void cargar();
    } finally {
      setEligiendo(null);
    }
  };

  const opciones = useMemo(() => {
    const lista = [...(datos?.opciones ?? [])];
    if (orden === 'rapida')
      lista.sort(
        (a, b) => (a.dias ?? Infinity) - (b.dias ?? Infinity) || a.costoLogistico - b.costoLogistico
      );
    else lista.sort((a, b) => a.costoLogistico - b.costoLogistico);
    return lista;
  }, [datos, orden]);
  const masBarata = useMemo(
    () => [...(datos?.opciones ?? [])].sort((a, b) => a.costoLogistico - b.costoLogistico)[0]?.id,
    [datos]
  );
  const masRapida = useMemo(() => {
    const con = (datos?.opciones ?? []).filter((o) => o.dias != null);
    return con.length > 1 ? [...con].sort((a, b) => a.dias! - b.dias!)[0]?.id : undefined;
  }, [datos]);
  const vencidas = (datos?.opciones ?? []).filter((o) => o.vencida).length;
  const sinDias = (datos?.opciones ?? []).every((o) => o.dias == null);
  const elegida = datos?.elegidaId ? opciones.find((o) => o.id === datos.elegidaId) : null;

  // El costo acumulado de arriba, el mismo de las demás pantallas: con la ruta
  // elegida ya trae los tributos (antes acá era FOB + logística y no coincidía).
  const acumulado = importacion
    ? {
        total: importacion.resumen.acumulado,
        fob: importacion.resumen.fob,
        moneda: importacion.moneda,
      }
    : { total: 0, fob: 0 };

  return (
    <>
      <EncabezadoDeComercia
        etiqueta="Comercia · Logística"
        titulo="Rutas"
        subtitulo="Opciones para llevar tu contenedor de origen a destino, con su costo logístico."
        acumulado={acumulado}
      />

      <PasosDeLogistica
        actual="rutas"
        importacion={importacion}
        siguiente={{
          texto: 'Ver costo nacionalizado',
          href: '/comercia/costo-nacionalizado',
          deshabilitado: !elegida,
          aviso: !elegida ? 'Elige una ruta' : undefined,
        }}
      />

      {faltaAlgo ? (
        <Falta importacion={importacion} />
      ) : cargando && !datos ? (
        <div className="flex items-center gap-3 rounded-tarjeta border bg-card px-6 py-10 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Calculando las rutas con tu tarifario…
        </div>
      ) : error ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-tarjeta border border-bloqueo-borde bg-bloqueo-suave px-5 py-4 text-sm text-bloqueo"
        >
          <span className="flex-1">{error}</span>
          <Button size="sm" variant="outline" onClick={() => void cargar()}>
            Reintentar
          </Button>
        </div>
      ) : datos ? (
        <>
          {vencidas > 0 && (
            // Los tonos de la maqueta, los mismos de «Para revisar» en Carga: durazno y naranja quemado.
            <div
              role="alert"
              className="flex flex-wrap items-center gap-3 rounded-2xl bg-[#FDEFE6] px-5 py-3.5 text-[15px] text-[#9A3412] dark:bg-alerta-suave/70 dark:text-alerta"
            >
              <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
              <p className="flex-1">
                <strong>
                  {vencidas === datos.opciones.length
                    ? datos.opciones.length === 1
                      ? 'La ruta usa tarifas vencidas.'
                      : `Las ${datos.opciones.length} rutas usan tarifas vencidas.`
                    : `${vencidas} de ${datos.opciones.length} rutas usan tarifas vencidas.`}
                </strong>{' '}
                Confirma con el agente antes de prometer el precio.
              </p>
              <Link to="/trade/tarifario" className="font-semibold underline underline-offset-4">
                Actualizar tarifario
              </Link>
            </div>
          )}
          {datos.sinTarifa.length > 0 && (
            <p className="rounded-2xl bg-[#FDEFE6] px-5 py-3 text-sm text-[#9A3412] dark:bg-alerta-suave/70 dark:text-alerta">
              Tu tarifario no tiene tarifas para {datos.sinTarifa.join(' ni ')} en esta ruta. Cambia
              el contenedor en{' '}
              <Link to="/comercia/logistica/carga" className="font-semibold underline">
                Carga
              </Link>
              .
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex flex-wrap items-baseline gap-3">
              <span className="font-titulo text-[22px] font-semibold">
                {cantidadDe(datos.opciones.length, 'ruta posible', 'rutas posibles')}
              </span>
              {datos.opciones.length > 0 && (
                <span className="text-[15px] text-muted-foreground">
                  toca una para ver el desglose
                </span>
              )}
            </h2>
            <div
              className="inline-flex rounded-full border bg-muted/60 p-1"
              role="radiogroup"
              aria-label="Ordenar rutas"
            >
              {(['barata', 'rapida'] as const).map((o) => (
                <button
                  key={o}
                  type="button"
                  role="radio"
                  aria-checked={orden === o}
                  disabled={o === 'rapida' && sinDias}
                  title={
                    o === 'rapida' && sinDias
                      ? 'Tu tarifario no trae los días de tránsito'
                      : undefined
                  }
                  onClick={() => setOrden(o)}
                  className={cn(
                    'rounded-full px-5 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                    orden === o
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {o === 'barata' ? 'Más barata' : 'Más rápida'}
                </button>
              ))}
            </div>
          </div>

          {datos.opciones.length === 0 ? (
            <section className="rounded-tarjeta border border-dashed bg-card px-6 py-10 text-center">
              <p className="font-semibold">No hay rutas en tu tarifario para esta carga.</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {datos.sinTarifa.length > 0
                  ? 'Cambia el contenedor en Carga, o carga en el tarifario las tarifas para ese contenedor.'
                  : (datos.avisos[0] ?? 'Revisa el origen, el destino o el tipo de contenedor.')}
              </p>
              {datos.totalSinFiltro > 0 && (
                <Button
                  className="mt-4"
                  variant="outline"
                  onClick={() => void verTodas()}
                  disabled={todas}
                >
                  Ver las {datos.totalSinFiltro} rutas por otros puertos
                </Button>
              )}
            </section>
          ) : (
            <div className="space-y-4">
              {opciones.map((o) => (
                <TarjetaDeRuta
                  key={o.id}
                  opcion={o}
                  importacion={importacion!}
                  abierta={abierta === o.id}
                  elegida={datos.elegidaId === o.id}
                  eligiendo={eligiendo === o.id}
                  masBarata={o.id === masBarata && datos.opciones.length > 1}
                  masRapida={o.id === masRapida}
                  onAlternar={() => setAbierta((a) => (a === o.id ? null : o.id))}
                  onElegir={() => void elegir(o)}
                />
              ))}
              {importacion?.puntoLlegada && datos.totalSinFiltro > datos.opciones.length && (
                <p className="text-sm text-muted-foreground">
                  Estás viendo las rutas por {ciudadDe(importacion.puntoLlegada)}.{' '}
                  <button
                    type="button"
                    onClick={() => void verTodas()}
                    disabled={todas}
                    className="font-semibold text-primary hover:underline"
                  >
                    Ver también las {datos.totalSinFiltro - datos.opciones.length} por otros puertos
                  </button>
                </p>
              )}
            </div>
          )}

          <p className="flex items-start gap-2 rounded-2xl bg-muted/50 px-5 py-3.5 text-sm text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <span>
              Los costos salen de tu tarifario: son tarifas negociadas en el pasado, no un precio
              prometido. El seguro es el {formatoPorcentaje(1.5, { decimalesMaximos: 1 })} sobre el
              FOB más el flete, y los servicios en destino (agente aduanero, manipuleo) salen de
              Datos Maestros › Servicios de flete. Confirma con tu agente antes de cerrar.
            </span>
          </p>
        </>
      ) : null}
    </>
  );
}

function Falta({ importacion }: { importacion: ImportacionDeComercia | null }) {
  const sinOrigen = !importacion?.origen || !importacion.destino;
  return (
    <section className="rounded-tarjeta border border-dashed bg-card px-6 py-12 text-center">
      <h2 className="font-titulo text-xl font-semibold">
        {sinOrigen ? 'Falta el origen y el destino' : 'Falta armar la carga'}
      </h2>
      <p className="mt-1 text-muted-foreground">
        Las rutas se calculan para tu carga, de tu origen a tu destino.
      </p>
      <Link
        to={sinOrigen ? '/comercia/logistica/origen-destino' : '/comercia/logistica/carga'}
        className={buttonVariants({ variant: 'degradado', className: 'mt-5 px-6' })}
      >
        {sinOrigen ? 'Elegir origen y destino' : 'Armar la carga'}
      </Link>
    </section>
  );
}

const ICONO_DE_MODO = { SEA: Ship, AIR: Plane, LAND: Truck } as Record<string, typeof Ship>;
const NOMBRE_DE_MODO: Record<string, string> = { SEA: 'mar', AIR: 'avión', LAND: 'camión' };

function TarjetaDeRuta({
  opcion: o,
  importacion,
  abierta,
  elegida,
  eligiendo,
  masBarata,
  masRapida,
  onAlternar,
  onElegir,
}: {
  opcion: OpcionDeRuta;
  importacion: ImportacionDeComercia;
  abierta: boolean;
  elegida: boolean;
  eligiendo: boolean;
  masBarata: boolean;
  masRapida: boolean;
  onAlternar: () => void;
  onElegir: () => void;
}) {
  const destino = importacion.destino ? ciudadDe(importacion.destino) : 'destino';
  const idDesglose = `desglose-${o.id.replace(/[^a-zA-Z0-9]/g, '')}`;
  return (
    <article
      className={cn(
        'rounded-tarjeta border bg-card shadow-sm transition-[border-color,box-shadow]',
        elegida ? 'border-2 border-orange-700 shadow-md' : 'hover:border-foreground/25'
      )}
    >
      <div className="flex flex-col gap-4 px-6 py-5 lg:flex-row lg:items-center">
        <div className="min-w-0 flex-1 space-y-3">
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className="font-titulo text-xl font-semibold">Vía {o.via ?? 'directa'}</span>
            <span className="text-[15px] text-muted-foreground">
              {o.transportistas.join(' + ')} · tránsito{' '}
              {o.dias != null ? `${o.dias} días` : 'sin dato'}
            </span>
            {masBarata && (
              <span className="rounded-full bg-ok-suave px-2.5 py-0.5 text-xs font-bold text-ok">
                Más económica
              </span>
            )}
            {masRapida && (
              <span className="rounded-full bg-info-suave px-2.5 py-0.5 text-xs font-bold text-info">
                Más rápida
              </span>
            )}
            {o.vencida && (
              <span className="rounded-full bg-alerta-suave px-2.5 py-0.5 text-xs font-bold text-alerta">
                Tarifa vencida
              </span>
            )}
            <button
              type="button"
              onClick={onAlternar}
              aria-expanded={abierta}
              aria-controls={idDesglose}
              className="inline-flex items-center gap-1 text-[15px] font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {abierta ? 'Ocultar desglose' : 'Ver desglose'}
              {abierta ? (
                <ChevronUp className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </p>
          <MiniRuta opcion={o} almacen={importacion.almacen} />
        </div>
        <div className="flex items-center gap-5 lg:justify-end">
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Costo logístico</p>
            <p className="font-titulo text-[30px] font-semibold leading-none text-orange-700 tabular-nums">
              {usd(o.costoLogistico)}
            </p>
            <p className="mt-1 text-sm text-muted-foreground tabular-nums">
              Total puesto <strong className="text-foreground">{usd(o.totalPuesto)}</strong> ·{' '}
              {sobreElFob(o.sobreFob)}
            </p>
          </div>
          <Button
            variant={elegida ? 'degradado' : 'outline'}
            className="min-w-[124px] rounded-full px-6 disabled:opacity-100"
            onClick={onElegir}
            disabled={eligiendo || elegida}
            aria-pressed={elegida}
          >
            {eligiendo ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : elegida ? (
              <Check className="h-4 w-4" aria-hidden="true" />
            ) : null}
            {elegida ? 'Elegida' : 'Elegir'}
          </Button>
        </div>
      </div>

      {abierta && (
        <div id={idDesglose} className="mx-6 mb-6 rounded-2xl bg-surface-sunken p-5">
          <p className="flex flex-wrap items-baseline gap-2">
            <span className="text-[17px] font-semibold">Desglose del costo logístico</span>
            <span className="text-sm text-muted-foreground">
              vía {o.via ?? 'directa'} · {o.transportistas.join(' + ')}
            </span>
          </p>
          <dl className="mt-3 divide-y">
            {o.desglose.map((c) => (
              <div
                key={c.categoria}
                className="grid grid-cols-[minmax(0,1fr)_auto_40px] items-center gap-3 py-2.5 sm:grid-cols-[200px_1fr_120px_64px] sm:gap-4"
              >
                <dt
                  className="text-[15px] font-medium"
                  title={c.detalle.map((d) => `${d.concepto}: ${usd(d.monto)}`).join('\n')}
                >
                  {c.nombre}
                  {!c.enCif && (
                    <span className="ml-1.5 text-xs text-muted-foreground">en destino</span>
                  )}
                </dt>
                {/*
                  Como la maqueta: la barra es la parte del total (el flete con
                  55 % llena poco más de la mitad) sobre un riel que se ve. Antes
                  la mayor llenaba todo y el riel era del color del fondo.
                */}
                <dd
                  aria-hidden="true"
                  className="hidden h-2 overflow-hidden rounded-full bg-[#EFECF4] dark:bg-white/10 sm:block"
                >
                  <span
                    className="block h-full rounded-full bg-orange-700"
                    style={{ width: `${Math.min(100, Math.max(1.5, c.porcentaje))}%` }}
                  />
                </dd>
                <dd className="whitespace-nowrap text-right text-[15px] font-semibold tabular-nums">
                  {usd(c.monto)}
                </dd>
                <dd className="text-right text-sm text-muted-foreground tabular-nums">
                  {formatoPorcentaje(c.porcentaje, { decimales: 0 })}
                </dd>
              </div>
            ))}
          </dl>
          <dl className="mt-1 border-t-2 border-foreground/80 pt-3 text-[15px]">
            <div className="flex justify-between gap-4 py-1">
              <dt className="font-semibold">Costo logístico</dt>
              <dd className="font-semibold text-orange-700 tabular-nums">
                {usd(o.costoLogistico)}
              </dd>
            </div>
            <div className="flex justify-between gap-4 py-1 text-muted-foreground">
              <dt>+ Contenedor (FOB)</dt>
              <dd className="tabular-nums">{usd(importacion.resumen.fob)}</dd>
            </div>
            <div className="flex justify-between gap-4 py-1">
              <dt className="font-semibold">Total puesto en {destino}</dt>
              <dd className="flex flex-col items-end whitespace-nowrap tabular-nums sm:flex-row sm:items-baseline sm:gap-4">
                <span className="font-semibold">{usd(o.totalPuesto)}</span>
                <span className="text-sm text-muted-foreground">{sobreElFob(o.sobreFob)}</span>
              </dd>
            </div>
          </dl>
          {o.porContenedor.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              El flete se cobra por contenedor:{' '}
              {o.porContenedor
                .map((c) => `${c.cantidad} × ${c.nombre} = ${usd(c.flete)}`)
                .join(' · ')}
              .
            </p>
          )}
          {o.avisos.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-alerta">
              {o.avisos.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}

/** «SZX —mar— ARI —camión— SCZ», con el nombre debajo de cada código. */
function MiniRuta({ opcion, almacen }: { opcion: OpcionDeRuta; almacen: string | null }) {
  const paradas = [
    { codigo: opcion.tramos[0]?.origenCodigo, nombre: opcion.tramos[0]?.origen ?? '' },
    ...opcion.tramos.map((t) => ({ codigo: t.destinoCodigo, nombre: t.destino })),
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      {paradas.map((p, i) => {
        const tramo = opcion.tramos[i];
        const Icono = tramo ? (ICONO_DE_MODO[tramo.modo] ?? Ship) : null;
        return (
          <Fragment key={`${p.nombre}-${i}`}>
            <div className="min-w-[64px] text-center">
              <p className="text-[15px] font-bold tracking-wide">
                {p.codigo ?? p.nombre.slice(0, 3).toUpperCase()}
              </p>
              <p className="text-xs leading-tight text-muted-foreground">{p.nombre}</p>
              {i === paradas.length - 1 && almacen && (
                <p className="max-w-[140px] truncate text-xs leading-tight text-muted-foreground">
                  {almacen}
                </p>
              )}
            </div>
            {tramo && Icono && (
              <div className="flex items-center gap-1.5 px-1 text-sm text-muted-foreground">
                <span className="h-px w-8 bg-border" />
                <Icono className="h-4 w-4" aria-hidden="true" />
                <span>{NOMBRE_DE_MODO[tramo.modo] ?? tramo.modo.toLowerCase()}</span>
                <span className="h-px w-8 bg-border" />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
